import { getAppVersion, getCommitNum } from "@/config/env"
import { User, userDisplayName } from "@/models/biz_model"
import { calculateMD5 } from "@/utils/tools"
import axios from "axios"
import { v4 as uuidv4 } from "uuid"

const http = axios.create({
  timeout: 150000,
  // withCredentials: true,
})

// 每条日志固定上报的业务上下文（对齐 android DataReport.data 全局 LogData）。
// 跨连接持久：不随 onDisconnect 清空，保证断连后的错误上报仍携带用户/企业/设备上下文。
const _reportContext: {
  company_id?: string
  gateway_ip?: string
  desktop_public_ip?: string
  idc_id?: string
  user?: string // 昵称（展示名）
  username?: string // 账号
  local_dev_id?: string
  desktop_id?: string
  configured_scene?: string
  device_region?: string
  device_id?: string
} = {}

// 设置固定上报的业务上下文（仅传入的字段会被覆盖）
export function setReportContext(
  ctx: Partial<typeof _reportContext>
): void {
  Object.assign(_reportContext, ctx)
}

function sanitizeDeviceId(id: unknown): string {
  if (id == null) return ""
  const str = String(id)
  if (str === "undefined" || str === "null") return ""
  return str
}

// 从localStorage获取数据的辅助函数
function getDataFromLocalStorage(account?: string | null) {
  try {
    // 获取用户信息
    const currentUserAccount = account || localStorage.getItem("currentUser")
    let userLocalStorage = ""
    let usernameLocalStorage = ""
    let localDevIdLocalStorage = ""
    let desktopIdLocalStorage = ""
    let deviceIdLocalStorage = ""
    let businessSceneLocalStorage = ""
    let regionLocalStorage = ""
    if (currentUserAccount) {
      const userListJson = localStorage.getItem("userList")
      if (userListJson) {
        const userList = JSON.parse(userListJson)
        const currentUser = userList[currentUserAccount]
        if (currentUser) {
          userLocalStorage = userDisplayName(currentUser as User) || ""
          usernameLocalStorage = currentUser.account || ""
          localDevIdLocalStorage = currentUser.localDevId || ""
          desktopIdLocalStorage = currentUser.desktopId || ""
          deviceIdLocalStorage = sanitizeDeviceId(currentUser.deviceId)
          businessSceneLocalStorage = currentUser.business_scene || ""
          regionLocalStorage = currentUser.region || ""
        }
      }
    }

    return {
      userLocalStorage,
      usernameLocalStorage,
      localDevIdLocalStorage,
      desktopIdLocalStorage,
      deviceIdLocalStorage,
      businessSceneLocalStorage,
      regionLocalStorage,
    }
  } catch (error) {
    console.warn("Error getting data from localStorage:", error)
    return {
      userLocalStorage: "",
      usernameLocalStorage: "",
      localDevIdLocalStorage: "",
      desktopIdLocalStorage: "",
      deviceIdLocalStorage: "",
      businessSceneLocalStorage: "",
      regionLocalStorage: "",
    }
  }
}

// 离线日志持久化缓存：断网或上报失败时将日志序列化存入 localStorage，
// 网络恢复或下次启动时批量补报。对齐 android DataReport._pendingLogs 语义
// （全量缓存所有事件），但升级为持久化存储——进程被杀后遗留事件仍可在
// 下次启动补报。
const PENDING_LOGS_KEY = "kc_pending_logs"
const MAX_PENDING = 1000 // 防止 localStorage 配额溢出，超出丢弃最旧

function readPending(): any[] {
  try {
    const raw = localStorage.getItem(PENDING_LOGS_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr : []
  } catch (e) {
    console.warn("DataReport readPending error: ", e)
    localStorage.removeItem(PENDING_LOGS_KEY)
    return []
  }
}

function enqueuePending(objs: any[]): void {
  if (objs.length === 0) return
  const queue = readPending().concat(objs)
  // 超出上限丢弃最旧的条目，保护 localStorage 配额
  const trimmed =
    queue.length > MAX_PENDING ? queue.slice(queue.length - MAX_PENDING) : queue
  try {
    localStorage.setItem(PENDING_LOGS_KEY, JSON.stringify(trimmed))
  } catch (e) {
    // 配额不足等异常：清空队列避免后续写入持续失败造成死循环
    console.warn("DataReport enqueuePending error: ", e)
    localStorage.removeItem(PENDING_LOGS_KEY)
  }
}

async function postLogObjects(objs: any[]): Promise<void> {
  const userkey = "cloud_desktop"
  const secret = "bdsds3E4q7NDWFxu8bvDDuaLPCD2d"
  const curTime = Date.now().toString()
  const md5Hash = calculateMD5(userkey + secret + curTime)
  const url = `https://api-cd-kingdata.ksyun.com/v3/log/general/${userkey}/Client?s=${md5Hash}&t=${curTime}`
  const date = new Date()
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDay()
  const hour = date.getHours()
  const minute = date.getMinutes()
  const second = date.getSeconds()
  const curDateStr = `${year}-${month}-${day} ${hour}:${minute}:${second}`

  await http.post(url, {
    batch_id: uuidv4(),
    log_array: objs,
    batch_date: curDateStr,
  })
}

async function flushPendingLogs(): Promise<void> {
  const pending = readPending()
  if (pending.length === 0) return
  try {
    await postLogObjects(pending)
    // 上报成功后清空缓存
    localStorage.removeItem(PENDING_LOGS_KEY)
  } catch (e) {
    // 仍失败：保留缓存，等待下次 online / 启动再试
    console.warn("DataReport flushPendingLogs error: ", e)
  }
}

async function reportLogs(logs: LogData[]) {
  const objs = logs.map((log) => log.toObject())

  if (!navigator.onLine) {
    enqueuePending(objs)
    return
  }

  try {
    await postLogObjects(objs)
  } catch (e) {
    console.warn("DataReport error: ", e)
    // 上报失败时缓存，待网络恢复后补报
    enqueuePending(objs)
  }
}

function reportLog(log: LogData) {
  reportLogs([log])
}

// 网络恢复时补报缓存的所有离线日志（signal_disconnect 等）。
window.addEventListener("online", () => {
  void flushPendingLogs()
})
// 模块加载时尝试补报上次进程遗留的缓存日志——持久化存储的核心价值，
// 使进程被杀后未上报的事件在下次启动仍可补报。
void flushPendingLogs()

/* 上报错误事件
 * @param errorId: 用于错误统计，之后可能被废弃
 * @param errCode: 错误唯一标识
 * @param data: 额外添加的数据
 **/
function reportError(errCode: number, data?: LogExtraData) {
  let logData: LogExtraData = { error_code: errCode }
  if (data) {
    logData = { ...logData, ...data }
  }
  reportLog(new LogData("error", logData))
}

export const DataReport = {
  reportError,
  reportLog,
  reportLogs,
}

export interface LogExtraData {
  localDevId?: string | null
  desktopId?: string | null
  deviceId?: string | null
  user?: string | null
  nickName?: string | null
  businessScene?: string | null
  region?: string | null
  [key: string]: any
}

export class LogData {
  readonly event: string
  readonly data?: LogExtraData
  readonly timestamp: number

  constructor(event: string, data?: LogExtraData) {
    this.event = event
    this.data = data
    this.timestamp = Date.now()
  }

  toObject(): any {
    const { ...other } = this.data ?? {}

    // 从localStorage获取数据（页面重载后 _reportContext 丢失，用 localStorage 回退）
    const {
      userLocalStorage,
      usernameLocalStorage,
      localDevIdLocalStorage,
      desktopIdLocalStorage,
      deviceIdLocalStorage,
      businessSceneLocalStorage,
      regionLocalStorage,
    } = getDataFromLocalStorage(_reportContext.username)

    const logData: any = {
      act: this.event,
      user_agent: navigator.userAgent,
      log_timestamp: this.timestamp,
      os: "web",
      app_version: getAppVersion() || "unknow",
      commit_num: getCommitNum() || "unknow", // 当前版本的 git commit hash

      // 每条固定上报的业务上下文（仅当非空时写入，与 act 一样每条携带；
      // 放在 ...other 之前，事件级 extra 可覆盖）
      ...(_reportContext.company_id ? { company_id: _reportContext.company_id } : {}),
      ...(_reportContext.gateway_ip ? { gateway_ip: _reportContext.gateway_ip } : {}),
      ...(_reportContext.desktop_public_ip
        ? { desktop_public_ip: _reportContext.desktop_public_ip }
        : {}),
      ...(_reportContext.idc_id ? { idc_id: _reportContext.idc_id } : {}),

      ...other,

      // 持久上下文优先，localStorage 作为页面重载后的回退
      user: _reportContext.user || userLocalStorage || _reportContext.username || "",
      username: _reportContext.username || usernameLocalStorage || "",
      local_dev_id: _reportContext.local_dev_id || localDevIdLocalStorage || "",
      desktop_id: _reportContext.desktop_id || desktopIdLocalStorage || "",
      configured_scene: _reportContext.configured_scene || businessSceneLocalStorage,
      device_region: _reportContext.device_region || regionLocalStorage,
    }

    // device_id 为空时不写入字段，避免向 long 类型字段写入空串导致解析失败。
    // 彻底修复需后端将 ES mapping 改为 keyword（device_id 为设备标识字符串）。
    const deviceIdValue = sanitizeDeviceId(
      _reportContext.device_id || deviceIdLocalStorage
    )
    if (deviceIdValue) {
      logData.device_id = deviceIdValue
    }

    return logData
  }
}

// 错误事件的id
